import React, { useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import { startOfToday } from 'date-fns';
import clsx from 'clsx';

import useIgnorableError from '@ecp/common/src/hooks/useIgnorableError';
import boTheme from '@ecp/common/src/style/theme/bo';
import { FlexBox, FlexBoxColumn } from '@ecp/common/src/layouts/flex/styled';
import CalendarUnstyled from '@ecp/common/src/components/unstyled/calendar/CalendarUnstyled';
import { calendarClassNames, CalenderPropTypes, StyledCalendar } from '@ecp/common/src/components/calendar/Calendar';
import { StyledErrorMessage } from '@ecp/common/src/components/input/bo/Input';
import useCalendar, { NO_END_DATE } from '@ecp/common/src/components/calendar/useCalendar';

const StyledBoCalendar = styled(StyledCalendar)`
  .react-datepicker {
    display: flex;
    flex-direction: column;
    gap: 10px;

    &__input {
      &-container {
        height: 28px;
        width: 106px;
        display: flex;

        ${({ inputStyle }) => inputStyle};

        input {
          padding: 0 7px;
          border-radius: 3px;
          border: 1px solid ${boTheme.color.sub6};
          font-size: ${boTheme.font.size.small};
          font-weight: ${boTheme.font.weight.regular};
          background-position: top 5px right 6px;
          background-size: 14px;

          &:focus-visible {
            border: 1px solid ${boTheme.color.main};
          }

          &[readonly] {
            background-color: ${boTheme.color.sub8};
            border: 1px solid ${boTheme.color.sub6};
          }
        }

        &.${calendarClassNames.readOnly} {
          background-color: #eeeeee;
        }
      }

      &-time-container {
        margin: 0;
        display: flex;
        align-items: center;
        justify-content: center;

        & > div.react-datepicker-time__caption {
          display: none;
        }

        & div.react-datepicker-time__input {
          margin: 0;
        }

        input.react-datepicker-time__input[type='time'] {
          display: flex;
          font-size: 12px;
          border: solid 1px rgb(204, 204, 204);
          padding: 0 4px;
          margin: 0;

          &::-webkit-datetime-edit-fields-wrapper {
            display: flex;
          }

          &::-webkit-datetime-edit-text {
            order: 3;
          }

          &::-webkit-datetime-edit-hour-field {
            order: 2;
          }

          //noinspection CssInvalidPseudoSelector
          &::-webkit-datetime-edit-minute-field {
            order: 4;
          }

          //noinspection CssInvalidPseudoSelector
          &::-webkit-datetime-edit-ampm-field {
            order: 1;
          }

          &::-webkit-clear-button {
            display: none;
          }

          &::-webkit-calendar-picker-indicator {
            display: none;
            //background-color: red;
          }
        }
      }
    }

    &__children {
      &-container {
        width: auto;
      }
    }
  }

  &.${calendarClassNames.error} {
    & input {
      border: 1px solid ${boTheme.color.error1};
    }
  }

  &.${calendarClassNames.readOnly} {
    pointer-events: none;
  }
`;

const Calendar = React.forwardRef(({ inputStyle, error, errorMessage, ...props }, ref) => {
  const { value, readOnly } = props;
  const { ignoreError } = useIgnorableError({ error, value });
  const stateClasses = {
    [calendarClassNames.error]: error,
    [calendarClassNames.readOnly]: readOnly,
  };

  return (
    <FlexBoxColumn align-items={'flex-start'}>
      <StyledBoCalendar inputStyle={inputStyle} className={clsx(stateClasses)}>
        <CalendarUnstyled {...props} ref={ref} error={!ignoreError && error} />
      </StyledBoCalendar>
      {!ignoreError && error && errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
    </FlexBoxColumn>
  );
});

Calendar.displayName = 'Calendar';

Calendar.propTypes = {
  ...CalenderPropTypes,
  showTimeInput: PropTypes.bool,
  readOnly: PropTypes.bool,
};

Calendar.defaultProps = {
  inputStyle: {},
};

const CalendarButton = styled(FlexBox)`
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 28px;
  background: ${boTheme.color.sub11};
  border: 1px solid ${boTheme.color.sub6};
  cursor: pointer;

  font-weight: ${boTheme.font.weight.bold};
  font-size: ${boTheme.font.size.sub};

  color: ${boTheme.color.sub1};
`;

export const CalendarRange = ({
  startDate,
  endDate,
  inputStyle,
  startMinDate,
  onStartChange,
  onEndChange,
  onAllDateChange,
  error,
  errorMessage,
  setNoEndDateToInput,
  ...props
}) => {
  const { selectOnly, disableStart, disableEndDateButton, disableTodayButton } = props;
  const { ignoreError } = useIgnorableError({ error, value: { startDate, endDate } });
  const { handleStartToday, handleStartChange, handleEndChange } = useCalendar({
    startMinDate,
    startDate,
    endDate,
    onStartChange,
    onEndChange,
    onAllDateChange,
  });
  const startRef = useRef();
  const endRef = useRef();

  const handleToday = useCallback(() => {
    handleStartToday();

    startRef?.current?.handleCalendarClickOutside();
  }, [handleStartToday]);

  const handleNoEndDate = useCallback(() => {
    onEndChange?.(setNoEndDateToInput ? new Date(NO_END_DATE) : null);
    endRef?.current?.handleCalendarClickOutside();
  }, [onEndChange, setNoEndDateToInput]);

  return (
    <FlexBoxColumn align-items={'flex-start'}>
      <FlexBox gap={'6px'}>
        <Calendar
          selectsStart
          {...props}
          readOnly={disableStart || props.readOnly}
          value={startDate}
          startDate={startDate}
          endDate={endDate}
          minDate={startMinDate}
          maxDate={endDate}
          onChange={handleStartChange}
          error={!ignoreError && error}
          inputStyle={inputStyle}
          ref={startRef}
        >
          {!disableTodayButton && <CalendarButton onClick={handleToday}>오늘</CalendarButton>}
        </Calendar>
        ~
        <Calendar
          selectsEnd
          {...props}
          value={endDate}
          startDate={startDate}
          endDate={endDate}
          minDate={startDate}
          onChange={handleEndChange}
          error={!ignoreError && error}
          inputStyle={inputStyle}
          ref={endRef}
        >
          {!selectOnly && !disableEndDateButton && (
            <CalendarButton onClick={handleNoEndDate}>종료일 없음</CalendarButton>
          )}
        </Calendar>
      </FlexBox>
      {!ignoreError && error && errorMessage && <StyledErrorMessage>{errorMessage}</StyledErrorMessage>}
    </FlexBoxColumn>
  );
};

CalendarRange.propTypes = {
  ...CalenderPropTypes,
  startMinDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.string]),
  onStartChange: PropTypes.func,
  onEndChange: PropTypes.func,
  selectOnly: PropTypes.bool,
  disableEndDateButton: PropTypes.bool,
  disableTodayButton: PropTypes.bool,
  setNoEndDateToInput: PropTypes.bool,
};

CalendarRange.defaultProps = {
  inputStyle: {},
  setNoEndDateToInput: false,
  disableEndDateButton: false,
  disableTodayButton: false,
};

export const CalendarDateTime = ({ inputStyle, onChange, ...props }) => {
  return (
    <Calendar
      showTimeInput
      timeInputLabel={'시간 :'}
      timeFormat={'H:mm aa'}
      dateFormat={'yyyy-MM-dd HH:mm'}
      inputStyle={inputStyle}
      onChange={onChange}
      {...props}
    />
  );
};

CalendarDateTime.propTypes = {
  ...CalenderPropTypes,
};

CalendarDateTime.defaultProps = {
  inputStyle: { width: '142px' },
};

export const CalendarDateTimeRange = ({ startDate, endDate, inputStyle, onStartChange, onEndChange, ...props }) => {
  return (
    <CalendarRange
      showTimeInput
      timeInputLabel={'시간 :'}
      timeFormat={'H:mm aa'}
      dateFormat={'yyyy-MM-dd HH:mm'}
      startDate={startDate}
      endDate={endDate}
      onStartChange={onStartChange}
      onEndChange={onEndChange}
      inputStyle={inputStyle}
      {...props}
    />
  );
};

CalendarDateTimeRange.propTypes = {
  ...CalenderPropTypes,
  onStartChange: PropTypes.func,
  onEndChange: PropTypes.func,
  disableStart: PropTypes.bool,
};

CalendarDateTimeRange.defaultProps = {
  inputStyle: { width: '142px' },
};

export const CalendarYearMonth = ({ inputStyle, ...props }) => {
  return <Calendar showMonthYearPicker inputStyle={inputStyle} dateFormat={'yyyy-MM'} {...props} />;
};

CalendarYearMonth.propTypes = {
  ...CalenderPropTypes,
};

CalendarYearMonth.defaultProps = {
  inputStyle: {},
};
export const CalendarYearMonthRange = ({ startDate, endDate, inputStyle, onStartChange, onEndChange, ...props }) => {
  return (
    <CalendarRange
      showMonthYearPicker
      inputStyle={inputStyle}
      dateFormat={'yyyy-MM'}
      startDate={startDate}
      endDate={endDate}
      onStartChange={onStartChange}
      onEndChange={onEndChange}
      {...props}
    />
  );
};

CalendarYearMonthRange.propTypes = {
  ...CalenderPropTypes,
};

CalendarYearMonthRange.defaultProps = {
  inputStyle: {},
};

export const CalendarDateTimeWithTodayButton = ({ inputStyle, onChange, dateFormat, ...props }) => {
  const handleToday = useCallback(() => {
    const today = startOfToday();
    onChange?.(today);
  }, [onChange]);

  return (
    <Calendar
      showTimeInput
      timeInputLabel={'시간 :'}
      timeFormat={'H:mm aa'}
      dateFormat={dateFormat}
      inputStyle={inputStyle}
      shouldCloseOnSelect={false}
      onChange={onChange}
      {...props}
    >
      <CalendarButton onClick={handleToday}>오늘</CalendarButton>
    </Calendar>
  );
};

CalendarDateTimeWithTodayButton.propTypes = {
  ...CalenderPropTypes,
  dateFormat: PropTypes.string,
  shouldCloseOnSelect: PropTypes.bool,
};

CalendarDateTimeWithTodayButton.defaultProps = {
  inputStyle: { width: '142px' },
  dateFormat: 'yyyy-MM-dd HH:mm',
  shouldCloseOnSelect: true,
};

export default Calendar;
